import type {
  AuthenticatedUser,
  IResource,
  Office,
  ResourceType
} from '@gettactic/api';

const ORG_SLUG_REQUIRES_RESERVATION_AFTER_PICKING_WL = Object.freeze([
  'tesla',
  'staging',
  'grammarly',
  'demo'
]);

// Redirects to health questionnaire / survey immediately after creating a reservation
const ORG_SLUG_RESERVATION_SURVEY = Object.freeze([
  'deighton',
  'pictonmahoney'
]);

const ORG_SLUG_PRIVACY_REPORTS = Object.freeze(['altair']);

const ORG_SLUG_NO_VISITOR_REPORTS = Object.freeze(['altair', 'hpe']);

const ORG_SLUG_NO_PAST_RESERVATIONS = Object.freeze(['softserve']);

const ORG_SLUG_RB_FEATURES = Object.freeze([
  'redbull',
  'staging',
  'chatarra',
  'alekhya'
]);

const ORG_SLUG_DOC_FEATURE = Object.freeze([
  'demo',
  'tactic',
  'staging',
  'sergio',
  'alekhya'
]);

const ORG_SLUG_EXTENDED_RESTRICTIONS = Object.freeze([
  'demo',
  'tactic',
  'staging',
  'alekhya',
  'exness',
  'kordamentha'
]);

const ORG_SLUG_MAP_BUILDER_EDITOR_ENABLED = Object.freeze([
  'demo',
  'tactic',
  'staging',
  'gondor',
  'softserve'
]);

/**
 * An array of user IDs that will force the recording of sessions in Sentry Session Replay.
 * */
const FORCE_SESSION_REPLAY_USERS: readonly string[] = Object.freeze([]);

const RESOURCE_ID_VIRTUAL_ROOMS = Object.freeze([
  'reso_af45af60-df41-4a69-aaec-64b01c0a643a', // Tesla - Desk 1
  'reso_ebe95805-38de-4637-84bd-e935b17ca8b2', // Tesla - Desk 2
  'reso_e00f796e-6fc1-4e55-b997-c41a52961ffc', // Tactic - Huddle Room #1
  'reso_6f8dc164-039c-4c64-a72d-f4bdabeb60ef', // Tactic - Huddle Room #2
  'reso_9d4c181a-a599-4ec3-8192-89c7f7e22c90', // Tactic - Huddle Room #3
  'reso_56194b05-2a58-460a-8a06-359029abc2ef' // Tactic - Huddle Room #4,
]);

const ORG_USER_DASH_BETA = Object.freeze([
  'user_4180c132-b8c2-4a31-89b0-b69d6ff19e15',
  'user_1dd31aae-68ad-4e27-8778-5378b861417e'
]);
const ORG_USER_OLD_DASHBOARD_AS_DEFAULT = Object.freeze([
  'user_4180c132-b8c2-4a31-89b0-b69d6ff19e15',
  'user_1dd31aae-68ad-4e27-8778-5378b861417e'
]);

export function isWifiAppEnabled(user?: AuthenticatedUser) {
  return (
    isTacticStaffOrImpersonated(user) ||
    user?.organization?.features.includes('wifi')
  );
}

export function isWorkingLocationsWithOOOFeaturesEnabled(
  user?: AuthenticatedUser
) {
  return user?.organization?.features.includes('leave.management');
}

export function isReservationSurveyEnabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_RESERVATION_SURVEY.includes(user?.organization?.slug ?? '')
  );
}

export function canUseMapBuilderEditor(user?: AuthenticatedUser) {
  return (
    user &&
    ORG_SLUG_MAP_BUILDER_EDITOR_ENABLED.includes(user?.organization?.slug ?? '')
  );
}

export function isPrivacyReportsEnabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_PRIVACY_REPORTS.includes(user?.organization?.slug ?? '')
  );
}

export function isNotAllowedToSelectAllOfficesForReports(
  user?: AuthenticatedUser
) {
  return user && ['hpe'].includes(user?.organization?.slug ?? '');
}

export function isVisitorReportDisabled(user?: AuthenticatedUser) {
  return (
    user && ORG_SLUG_NO_VISITOR_REPORTS.includes(user?.organization?.slug ?? '')
  );
}

export function hasEnabledDeleteResource(
  user?: AuthenticatedUser,
  resource?: IResource
) {
  return isTacticStaffOrImpersonated(user) || resource?.type === 'area';
}

export function hasEnabledTacticVirtualRooms(user?: AuthenticatedUser) {
  return isTacticStaffOrImpersonated(user);
}

export function isTacticVirtualRoom(resource: IResource) {
  return RESOURCE_ID_VIRTUAL_ROOMS.includes(resource.id ?? '');
}

export function isSingleUserDashBeta(user?: AuthenticatedUser) {
  return user && ORG_USER_DASH_BETA.includes(user?.user?.id ?? '');
}

export function isOldDashboardDefault(user?: AuthenticatedUser) {
  return (
    user && ORG_USER_OLD_DASHBOARD_AS_DEFAULT.includes(user?.user?.id ?? '')
  );
}

export function isNoPastReservations(user?: AuthenticatedUser) {
  return (
    user &&
    ORG_SLUG_NO_PAST_RESERVATIONS.includes(user?.organization?.slug ?? '')
  );
}

export function isRBFeature(user?: AuthenticatedUser) {
  return user && ORG_SLUG_RB_FEATURES.includes(user?.organization?.slug ?? '');
}

export function isHpe(user?: AuthenticatedUser) {
  return ['hpe', 'hpe-test'].includes(user?.organization?.slug ?? '');
}

export function allowShowDashboardStats(user?: AuthenticatedUser) {
  return isOrganizationAdmin(user) || !isHpe(user);
}

export function isMapAssignment(user?: AuthenticatedUser) {
  return !!user;
}

export function isDocFeatureEnabled(user?: AuthenticatedUser) {
  return user && ORG_SLUG_DOC_FEATURE.includes(user?.organization?.slug ?? '');
}

export function isSessionReplayForcedForUser(user?: AuthenticatedUser) {
  if (!user) {
    return;
  }
  if (user?.user?.is_being_impersonated) {
    return;
  }
  return FORCE_SESSION_REPLAY_USERS.includes(user?.user?.id ?? '');
}

export type DefaultHoursForOffice = {
  startHour: number;
  endHour: number;
  meetingRoomDuration: number;
  deskDuration: number;
  endMinutes: number;
  startMinutes: number;
  overrideOperationalHours?: boolean;
};

const NO_USER_INTEGRATION_ORGS = Object.freeze({
  'orgz_e92857ae-c10b-48a0-8e47-5608a9b60a5c': 'veeam'
});

const SKIP_MAP_CLICK_ORG = Object.freeze(['instructure']);

export function isHealthModuleRestrictionsEnabled(
  user?: AuthenticatedUser,
  office?: Office
) {
  return user && !!office?.health_setting;
}

export function isHealthModuleEnabled(user?: AuthenticatedUser) {
  return !!user && !!user?.organization?.health_module_enabled;
}

export function isHealthAdmin(user?: AuthenticatedUser) {
  if (user?.user?.organization_user.health_is_manager) {
    return true;
  }
  return false;
}

/**
 * Shows a custom message for the currentOffice, not the default office
 */
export function getCustomOfficeDashboardMessage(officeId: string) {
  return officeId in CUSTOM_OFFICE_BANNER_MESSAGES
    ? CUSTOM_OFFICE_BANNER_MESSAGES[
        officeId as keyof typeof CUSTOM_OFFICE_BANNER_MESSAGES
      ]
    : '';
}

const CUSTOM_OFFICE_BANNER_MESSAGES = {
  'offi_7e9bad16-c43f-4cc8-bf5c-ccc670ba2283':
    'Reminder to close all doors and shut off all lights', // 1010Data, NYC
  'offi_329d0b4a-9d68-42fa-9d4f-b11770ba4106':
    'Reminder to close all doors and shut off all lights', // Gondor, Main Office 3
  'offi_2796ada9-a851-4b8a-aabc-caaa04887eea':
    "Please don't forget to bring a mouse and laptop charger!", // Nitro Toronto
  'offi_cea10108-9e5f-4267-b4aa-b938d46af296':
    "Please don't forget to bring a mouse and laptop charger!" // Nitro Dublin
};

const FACILITIES_TICKETS = [
  'orgz_82512be1-a752-4723-bc18-609a84a3b067' // netdocuments
];

export function isFacilityTicketEnabled(organizationId: string) {
  return FACILITIES_TICKETS.includes(organizationId);
}

export function isTacticStaff(user?: AuthenticatedUser) {
  return (
    user?.user?.email &&
    (user.user.email.endsWith('@gettactic.com') ||
      user.user.email.endsWith('@tactic-staging.com') ||
      user.user.email.endsWith('@gettactic.onmicrosoft.com') ||
      process.env.NODE_ENV === 'development')
  );
}

export function isTacticStaffOrImpersonated(user?: AuthenticatedUser) {
  return isTacticStaff(user) || user?.user?.is_being_impersonated;
}

export function isOrganizationAdmin(user?: AuthenticatedUser) {
  return user?.user?.organization_user.roles.includes('owner');
}

export function isLabelFieldEnabled(user?: AuthenticatedUser) {
  return isTacticStaffOrImpersonated(user);
}

export function isRecurringEnabled(user?: AuthenticatedUser) {
  return (
    isTacticStaff(user) ||
    user?.organization?.features.includes('recurring') ||
    isSingleUserDashBeta(user)
  );
}

export function isRecurringDeskDisabled(user?: AuthenticatedUser) {
  return user?.organization?.features.includes('recurring.disable.desk');
}

export function isRecurringRoomDisabled(user?: AuthenticatedUser) {
  return user?.organization?.features.includes('recurring.disable.room');
}

export function showRecurring(
  resourceType: ResourceType,
  authenticatedUser: AuthenticatedUser,
  office: Office
) {
  const canScheduleRecurring =
    authenticatedUser.user?.organization_user.can_schedule_recurring;
  const canScheduleRecurringEnabled =
    authenticatedUser.user?.organization_user.can_schedule_recurring_enabled;

  // Admin only
  if (canScheduleRecurring) {
    return true;
  }

  // If not an admin, check office + resource type combination
  if (canScheduleRecurringEnabled) {
    if (!office.allow_recurring) {
      return false;
    }

    // Either allow_recurring_types is null, an empty array, or contains the resource type
    if (
      !office.allow_recurring_types ||
      (office.allow_recurring_types &&
        (office.allow_recurring_types.length === 0 ||
          office.allow_recurring_types.includes(resourceType)))
    ) {
      return true;
    }
  }

  return false;
}

export function isWorkingLocationsEnabled(user?: AuthenticatedUser) {
  return (
    isTacticStaff(user) ||
    user?.organization?.features.includes('dashboard.new') ||
    isSingleUserDashBeta(user)
  );
}

export function orgRequiresReservationAfterPickingAnOffice(
  user?: AuthenticatedUser
) {
  return ORG_SLUG_REQUIRES_RESERVATION_AFTER_PICKING_WL.includes(
    user?.organization?.slug ?? ''
  );
}

export function isTemporarySkipMapClickOrg(user?: AuthenticatedUser) {
  return user && SKIP_MAP_CLICK_ORG.includes(user?.organization?.slug ?? '');
}

export function isPersonalIntegrationsDisabled(user?: AuthenticatedUser) {
  return (
    user?.organization !== null &&
    NO_USER_INTEGRATION_ORG_IDS.find((x) => x === user?.organization?.id)
  );
}

/**
 * ID's exported to separate pages
 */

export const NO_USER_INTEGRATION_ORG_IDS = Object.freeze(
  Object.keys(NO_USER_INTEGRATION_ORGS)
);

/**
 * Hide restriction / permissions UI for zones until API is ready.
 * See https://github.com/gettactic/api/issues/69
 */
export const ZONES_RESTRICTED_UI = false;
